import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JwtService } from 'src/app/services/jwt.service';
import { MixpanelService } from 'src/app/services/mixpanel.service';

@Component({
    selector: 'app-video-library-upload',
    templateUrl: './video-library-upload.component.html',
    styleUrls: ['./video-library-upload.component.css']
})
export class VideoLibraryUploadComponent implements OnInit, OnDestroy {
    // Form data model
    videoLibrary = {
        client: null as any,
        excelFile: null as any,
        zipFile: null as any,
        commit: false
    };

    // State management
    uploading = false;
    jobStatus: any = null;
    private pollingInterval: any = null;
    private currentJobId: string | null = null;
    currentStep = 1; // 1: Upload, 2: Processing, 3: Complete

    // Available clients from sessionStorage
    clients = JSON.parse(sessionStorage.getItem('clients'));

    clientControl = new FormControl();
    filteredClients: Observable<any[]>;

    constructor(
        private api: ApiService,
        private snackbar: MatSnackBar,
        private jwt: JwtService,
        private mixpanel: MixpanelService
    ) { }

    ngOnInit(): void {
        // Track page view
        this.mixpanel.track('page_view', {
            page: 'Video Library Upload',
            category: 'Dashboard',
            button: '-'
        });

        // Validate JWT token
        this.jwt.check();

        this.filteredClients = this.clientControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterClients(value || ''))
        );
    }

    /**
     * Filter clients based on user input
     */
    private _filterClients(value: string): any[] {
        if (typeof value === 'object' && value !== null) {
            return this.clients;
        }

        const filterValue = value.toLowerCase();
        return this.clients.filter((client: any) =>
            client.dname.toLowerCase().includes(filterValue) ||
            client.id.toString().toLowerCase().includes(filterValue)
        );
    }

    /**
     * Display function for autocomplete
     */
    displayClient(client: any): string {
        return client?.dname || '';
    }

    /**
     * Handle client selection
     */
    onClientSelected(client: any): void {
        this.videoLibrary.client = client?.id || null;
    }

    ngOnDestroy(): void {
        // Clean up polling interval to prevent memory leaks
        this.stopPolling();
    }

    /**
     * Handle Excel file selection
     */
    onExcelFileSelected(event: any): void {
        this.videoLibrary.excelFile = event.target.files[0] ?? null;
    }

    /**
     * Handle ZIP file selection
     */
    onZipFileSelected(event: any): void {
        this.videoLibrary.zipFile = event.target.files[0] ?? null;
    }

    /**
     * Remove Excel file
     */
    removeExcelFile(): void {
        this.videoLibrary.excelFile = null;
    }

    /**
     * Remove ZIP file
     */
    removeZipFile(): void {
        this.videoLibrary.zipFile = null;
    }

    /**
     * Upload video library content
     */
    upload(): void {
        // Track button click
        this.mixpanel.track('button_click', {
            page: 'Video Library Upload',
            category: 'Dashboard',
            button: this.videoLibrary.commit ? 'Upload' : 'Validate'
        });

        // Prepare form data
        const formData = new FormData();
        formData.append('client', this.videoLibrary.client);
        formData.append('excelFile', this.videoLibrary.excelFile);
        formData.append('zipFile', this.videoLibrary.zipFile);
        formData.append('commit', this.videoLibrary.commit ? 'yes' : 'no');

        // Set uploading state
        this.uploading = true;
        this.jobStatus = null;
        this.currentStep = 2; // Move to processing step

        // Call upload API
        this.api.videoLibraryUpload(formData).subscribe({
            next: (response) => {
                this.uploading = false;

                if (response.status && response.data && response.data.jobId) {
                    this.snackbar.open(
                        'Upload started! Tracking progress...',
                        undefined,
                        {
                            duration: 3000,
                            panelClass: 'success'
                        }
                    );

                    // Start polling for status
                    this.startPolling(response.data.jobId);
                } else {
                    this.snackbar.open(
                        'Upload failed: Invalid response from server',
                        undefined,
                        {
                            duration: 5000,
                            panelClass: 'failure'
                        }
                    );
                }
            },
            error: (error) => {
                this.uploading = false;
                console.error('Upload failed:', error);
            }
        });
    }

    /**
     * Start polling for job status (every 5 seconds)
     */
    private startPolling(jobId: string): void {
        this.currentJobId = jobId;

        this.checkStatus();

        this.pollingInterval = setInterval(() => {
            this.checkStatus();
        }, 5000);
    }

    /**
     * Check current job status
     */
    private checkStatus(): void {
        if (!this.currentJobId) return;

        this.api.videoLibraryStatus(this.currentJobId).subscribe({
            next: (response) => {
                if (response.status && response.data) {
                    this.jobStatus = response.data;

                    // Stop polling if job is complete (SUCCESS or FAILED)
                    if (
                        response.data.status === 'SUCCESS' ||
                        response.data.status === 'FAILED'
                    ) {
                        this.stopPolling();
                        this.currentStep = 3; // Move to complete step

                        // Show completion notification
                        if (response.data.status === 'SUCCESS') {
                            this.snackbar.open(
                                'Upload completed successfully!',
                                undefined,
                                {
                                    duration: 5000,
                                    panelClass: 'success'
                                }
                            );
                        } else {
                            this.snackbar.open(
                                'Upload failed. Please check the error details.',
                                undefined,
                                {
                                    duration: 5000,
                                    panelClass: 'failure'
                                }
                            );
                        }
                    }
                }
            },
            error: (error) => {
                console.error('Status check failed:', error);
            }
        });
    }

    /**
     * Stop polling interval
     */
    private stopPolling(): void {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    /**
     * Directly proceed to upload with commit=true from Step 1
     * Skips validation step — uploads immediately
     */
    proceedToUploadDirect(): void {
        this.videoLibrary.commit = true;
        this.upload();
    }

    /**
     * Promote successful validation to actual upload (called from Step 3)
     */
    promoteToUpload(): void {
        this.videoLibrary.commit = true;
        this.upload();
    }

    /**
     * Reset form to upload again
     */
    resetForm(): void {
        this.videoLibrary = {
            client: null,
            excelFile: null,
            zipFile: null,
            commit: false
        };
        this.clientControl.reset();
        this.jobStatus = null;
        this.currentJobId = null;
        this.currentStep = 1; // Reset to upload step
        this.stopPolling();
    }

    /**
     * Get status icon based on job status
     */
    getStatusIcon(): string {
        if (!this.jobStatus) return '';

        switch (this.jobStatus.status) {
            case 'SUCCESS':
                return 'check_circle';
            case 'FAILED':
                return 'error';
            case 'PROCESSING':
                return 'hourglass_empty';
            case 'PENDING':
                return 'schedule';
            default:
                return 'info';
        }
    }

    /**
     * Get status color based on job status
     */
    getStatusColor(): string {
        if (!this.jobStatus) return '';

        switch (this.jobStatus.status) {
            case 'SUCCESS':
                return 'primary';
            case 'FAILED':
                return 'warn';
            default:
                return 'accent';
        }
    }
}